import { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { Accordion } from "react-bootstrap"
import Layout from "../../components/Layout"
import * as permissionsApis from "../../utilities/apis/permissions"

// keys use spaces, the backend swaps them with underscores on save/read
// (e.g. "READ CASH FLOW OUTS" is checked as READ_CASH_FLOW_OUTS)
const rows = [
  { feature: "ROLE", update: "UPDATE ROLE", readAll: "READ ROLES", read: "READ ROLE" },
  { feature: "USER", create: "CREATE USER", update: "UPDATE USER", delete: "DELETE USER", readAll: "READ USERS", read: "READ USER" },
  { feature: "CATEGORY", create: "CREATE CATEGORY", update: "UPDATE CATEGORY", delete: "DELETE CATEGORY", readAll: "READ CATEGORIES", read: "READ CATEGORY" },
  { feature: "PRODUCT", create: "CREATE PRODUCT", update: "UPDATE PRODUCT", delete: "DELETE PRODUCT", readAll: "READ PRODUCTS", read: "READ PRODUCT" },
  { feature: "DEAL", create: "CREATE DEAL", update: "UPDATE DEAL", delete: "DELETE DEAL", readAll: "READ DEALS", read: "READ DEAL" },
  { feature: "ZONE", create: "CREATE ZONE", update: "UPDATE ZONE", delete: "DELETE ZONE", readAll: "READ ZONES", read: "READ ZONE" },
  {
    feature: "CUSTOMER", create: "CREATE CUSTOMER", update: "UPDATE CUSTOMER", delete: "DELETE CUSTOMER", readAll: "READ CUSTOMERS", read: "READ CUSTOMER",
    extras: [
      { key: "UPDATE LAST CAMPAIGN", label: "UPDATE LAST CAMPAIGN" }
    ]
  },
  { feature: "CUSTOMER ACTIVITY", readAll: "READ CUSTOMER ACTIVITIES" },
  { feature: "DRIVER", create: "CREATE DRIVER", update: "UPDATE DRIVER", delete: "DELETE DRIVER", readAll: "READ DRIVERS", read: "READ DRIVER" },
  {
    feature: "ORDER", create: "CREATE ORDER", update: "UPDATE ORDER", delete: "DELETE ORDER", readAll: "READ ORDERS", read: "READ ORDER",
    extras: [
      { key: "PAY ORDER", label: "PAY" },
      { key: "RECOVER ORDER", label: "RECOVER" },
      { key: "TRASH ORDER", label: "TRASH" },
      { key: "RESTORE ORDER", label: "RESTORE" },
      { key: "UPDATE DRIVER NOTE", label: "DRIVER NOTE" }
    ]
  },
  {
    feature: "ORDER REQUEST", create: "CREATE ORDER REQUEST", readAll: "READ ORDER REQUESTS", read: "READ ORDER REQUEST",
    extras: [
      { key: "COMPLETE ORDER REQUEST", label: "COMPLETE" },
      { key: "COMPLETE ORDER REQUESTS", label: "BULK COMPLETE" },
      { key: "REJECT ORDER REQUEST", label: "REJECT" },
      { key: "REJECT ORDER REQUESTS", label: "BULK REJECT" }
    ]
  },
  {
    feature: "PAYMENT REQUEST", create: "CREATE REQUEST", update: "UPDATE REQUEST", delete: "DELETE REQUEST", readAll: "READ REQUESTS", read: "READ REQUEST",
    extras: [
      { key: "COMPLETE REQUEST", label: "COMPLETE" },
      { key: "COMPLETE REQUESTS", label: "BULK COMPLETE" }
    ]
  },
  { feature: "COMPLAINT", create: "CREATE COMPLAINT", update: "UPDATE COMPLAINT", delete: "DELETE COMPLAINT", readAll: "READ COMPLAINTS", read: "READ COMPLAINT" },
  {
    feature: "CAMPAIGN", create: "CREATE CAMPAIGN", update: "UPDATE CAMPAIGN", delete: "DELETE CAMPAIGN", readAll: "READ CAMPAIGNS", read: "READ CAMPAIGN",
    extras: [
      { key: "SENT CAMPAIGN", label: "SENT" },
      { key: "RESPONDED CAMPAIGN", label: "RESPONDED" }
    ]
  },
  { feature: "SHORTCUT MESSAGE", create: "CREATE SHORTCUT MESSAGE", update: "UPDATE SHORTCUT MESSAGE", delete: "DELETE SHORTCUT MESSAGE", readAll: "READ SHORTCUT MESSAGES", read: "READ SHORTCUT MESSAGE" },
  {
    feature: "INCOME STATEMENT", create: "CREATE INCOME STATEMENT", update: "UPDATE INCOME STATEMENT", delete: "DELETE INCOME STATEMENT", readAll: "READ INCOME STATEMENTS", read: "READ INCOME STATEMENT",
    extras: [
      { key: "DELETE INCOME STATEMENTS", label: "BULK DELETE" }
    ]
  },
  { feature: "CASH FLOW OUT", create: "CREATE CASH FLOW OUT", update: "UPDATE CASH FLOW OUT", delete: "DELETE CASH FLOW OUT", readAll: "READ CASH FLOW OUTS", read: "READ CASH FLOW OUT" },
  {
    feature: "CASH FLOW IN", create: "CREATE CASH FLOW IN", update: "UPDATE CASH FLOW IN", delete: "DELETE CASH FLOW IN", readAll: "READ CASH FLOW INS", read: "READ CASH FLOW IN",
    note: "Backend also requires the matching Cash Flow Out permission"
  },
  {
    feature: "REPORT",
    extras: [
      { key: "GET SUMMARY", label: "SUMMARY" },
      { key: "GET ORDERS", label: "ORDERS" },
      { key: "GET ORDER COUNT VALUE", label: "ORDER COUNT VALUE" },
      { key: "GET ORDER CUSTOMER RATIO", label: "ORDER CUSTOMER RATIO" },
      { key: "GET ORDERS BY ZONE", label: "ORDERS BY ZONE" },
      { key: "GET ORDERS BY DRIVER", label: "ORDERS BY DRIVER" },
      { key: "GET ORDERS BY ACTIVE DRIVERS", label: "ORDERS BY ACTIVE DRIVERS" },
      { key: "GET CUSTOMER COUNT VALUE", label: "CUSTOMER COUNT VALUE" },
      { key: "GET CUSTOMER ZONE COUNT", label: "CUSTOMER ZONE COUNT" },
      { key: "GET ZONE COUNT VALUE", label: "ZONE COUNT VALUE" },
      { key: "GET RIDER COUNT VALUE", label: "RIDER COUNT VALUE" },
      { key: "GET PRODUCTS", label: "PRODUCTS" },
      { key: "GET PRODUCT BY", label: "PRODUCT BY" },
      { key: "GET INCOME STATEMENT", label: "INCOME STATEMENT" }
    ]
  },
  { feature: "INSIGHTS", readAll: "READ INSIGHTS" }
]

const actions = {
  create: "CREATE",
  update: "UPDATE",
  delete: "DELETE",
  readAll: "VIEW ALL",
  read: "VIEW"
}

const getRowPermissions = (row) => [
  ...Object.keys(actions).filter((action) => row[action]).map((action) => ({ key: row[action], label: actions[action] })),
  ...(row.extras || [])
]

const Permissions = () => {
  const { token, permissions: userPermissions } = useSelector((state) => state.user)

  const [roles, setRoles] = useState([])
  const [id, setId] = useState(null)
  const [permissions, setPermissions] = useState({})
  const [expanded, setExpanded] = useState([])

  const canUpdate = !!userPermissions["UPDATE_ROLE"]
  const allExpanded = expanded.length === rows.length

  const handleExpandAll = () => {
    setExpanded(allExpanded ? [] : rows.map((row) => row.feature))
  }

  const handleToggleRow = (feature) => {
    setExpanded((prevExpanded) => prevExpanded.includes(feature)
      ? prevExpanded.filter((key) => key !== feature)
      : [...prevExpanded, feature])
  }

  const handleChange = (e) => {
    setId(parseInt(e.target.value))
  }

  const handleCheckboxChange = (permission) => {
    setPermissions((prevPermissions) => ({
      ...prevPermissions,
      [permission]: !prevPermissions[permission],
    }))
  }

  const handleToggleAll = (keys) => {
    setPermissions((prevPermissions) => {
      const value = !keys.every((key) => prevPermissions[key])
      return keys.reduce((acc, key) => ({ ...acc, [key]: value }), { ...prevPermissions })
    })
  }

  const handleSave = async () => {
    try {
      const response = await permissionsApis.updateRole(token, { id, permissions })
      if (!response.status) throw new Error(response.message)

      alert("Permissions saved")
    } catch (error) {
      alert(error.message)
    }
  }

  const getRoles = async () => {
    try {
      const response = await permissionsApis.getRoles(token, "")
      if (!response.status) throw new Error(response.message)

      setRoles(response.roles)
      if (response.roles.length) setId(response.roles[0].id)
    } catch (error) {
      alert(error.message)
    }
  }

  const getRole = async () => {
    try {
      if (!id) return

      const response = await permissionsApis.getRole(token, id)
      if (!response.status) throw new Error(response.message)

      setPermissions(JSON.parse(response.role.permissions || "{}") || {})
    } catch (error) {
      alert(error.message)
    }
  }

  useEffect(() => {
    getRoles()
  }, [])

  useEffect(() => {
    getRole()
  }, [id])

  const renderCheckbox = (key, label) => {
    const inputId = `permission-${key.replace(/ /g, "-").toLowerCase()}`
    return (
      <div className="form-check">
        <input
          className="form-check-input"
          type="checkbox"
          id={inputId}
          checked={!!permissions[key]}
          disabled={!canUpdate}
          onChange={() => handleCheckboxChange(key)}
        />
        <label className="form-check-label small text-capitalize" htmlFor={inputId}>{label.toLowerCase()}</label>
      </div>
    )
  }

  return (
    <Layout
      header={true}
      footer={true}
    >
      <main id="permissions" className="py-3">
        <div className="container">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <select
              value={id ?? ""}
              onChange={handleChange}
              className="form-select rounded-4 text-capitalize"
              style={{ width: "25%" }}
            >
              {roles.map((role) => (
                <option key={role.id.toString()} value={role.id} className="text-capitalize">{role.name}</option>
              ))}
            </select>
            <div className="d-flex gap-2">
              <button className="btn btn-secondary rounded-4" onClick={handleExpandAll}>{allExpanded ? "Collapse All" : "Expand All"}</button>
              {canUpdate && <button className="btn btn-theme rounded-4" onClick={handleSave} disabled={!id}>Save</button>}
            </div>
          </div>

          <Accordion alwaysOpen activeKey={expanded} className="border rounded-4 overflow-hidden">
            {rows.map((row) => {
              const rowPermissions = getRowPermissions(row)
              const keys = rowPermissions.map((permission) => permission.key)
              const granted = keys.filter((key) => permissions[key]).length
              const isOpen = expanded.includes(row.feature)

              return (
                <Accordion.Item key={row.feature} eventKey={row.feature}>
                  {/* custom header so the checkbox sits beside the toggle button, not inside it */}
                  <h2 className="accordion-header position-relative">
                    <input
                      className="form-check-input position-absolute top-50 translate-middle-y m-0"
                      style={{ left: "1.25rem", fontSize: "1rem", zIndex: 4 }}
                      type="checkbox"
                      aria-label={`Select all ${row.feature.toLowerCase()} permissions`}
                      ref={(el) => { if (el) el.indeterminate = granted > 0 && granted < keys.length }}
                      checked={granted === keys.length}
                      disabled={!canUpdate}
                      onChange={() => handleToggleAll(keys)}
                    />
                    <button
                      type="button"
                      className={`accordion-button ${isOpen ? "" : "collapsed"}`}
                      style={{ paddingLeft: "3rem" }}
                      aria-expanded={isOpen}
                      onClick={() => handleToggleRow(row.feature)}
                    >
                      <div className="d-flex justify-content-between align-items-center w-100 me-3">
                        <span className="text-capitalize">{row.feature.toLowerCase()}</span>
                        <span className={`badge rounded-pill bg-light border ${granted ? "text-dark" : "text-muted"}`}>{granted}/{keys.length}</span>
                      </div>
                    </button>
                  </h2>
                  <Accordion.Body>
                    {row.note && <div className="small text-muted mb-2">{row.note}</div>}
                    <div className="row">
                      {rowPermissions.map((permission) => (
                        <div key={permission.key} className="col-6 col-md-3 mb-1">
                          {renderCheckbox(permission.key, permission.label)}
                        </div>
                      ))}
                    </div>
                  </Accordion.Body>
                </Accordion.Item>
              )
            })}
          </Accordion>
        </div>
      </main>
    </Layout>
  )
}

export default Permissions
